import React, { useEffect, useState } from "react";
import { FormControl, MenuItem, Select } from "@mui/material";
import Tooltip from '@mui/material/Tooltip';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { PropertyObj } from "../../../Models/PropertyObj";
import { useTranslation } from "react-i18next";

const theme = createTheme({
    components: {
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backgroundColor: '#ffffff',
                    padding: "10px",
                    color: 'rgba(0, 0, 0, 0.6)',
                    boxShadow: '0px 4px 48px rgba(0, 0, 0, 0.1)',
                    fontFamily: 'Plus Jakarta Sans',
                    fontStyle: "normal",
                    fontWeight: "500",
                    fontSize: "12px",
                    lineHeight: "13px",
                    width: "100%"
                },
                arrow: {
                    color: '#ffffff',
                },
            },
        },
    },
});

const PropertyDetails: React.FunctionComponent<{ property: any, errors: any, onchangeProperty: any, onchangeSize: any }> = (props) => {

    const [listCountry, setListPropertyTypes] = useState<any>({
        "UK": {
            "residential": ["Bungalows", "Detached", "Semi-detached", "Terraced", "houses", "Flats"],
            "commercial": ["Office", "Retail", "Warehouse", "Industrial", "Mixed Use"],
            "land": ["Residential Land", "Commercial Land", "Agricultural Land", "Industrial Land", "Mixed Use Land"],
            "price_currency": ['GBP', 'USD', 'EUR'],
        },
        "UAE": {
            "residential": ["Apartment", "Loft Apartment", "Hotel Apartment", "Villa", "Villa Compound", "Full Floor Residential", "Land Residential", "Residential Building", "Townhouse", "Penthouse", "Duplex", "Bungalow"],
            "commercial": ["Office", "Shop", "Warehouse", "Industrial", "Mixed Use", "Bulk Units", "Multiple Sale Units", "Outlet", "Showroom"],
            "land": ["Residential Land", "Commercial Land", "Agricultural Land", "Industrial Land", "Mixed Use Land"],
            "price_currency": ['AED', 'SAR', 'USD', 'EUR'],
        },
        "ME": {
            "residential": ["Apartment", "New building", "Villa", "House", "Land"],
            "commercial": ["Hotel", "Commercial real estate", "Office", "Retail", "Outlet", "Showroom"],
            "land": ["Residential Land", "Commercial Land", "Agricultural Land", "Industrial Land", "Mixed Use Land"],
            "price_currency": ["EUR"],
        },
        "FR": {
            "residential": ["Apartment", "Villa", "House", "Townhouse", "Penthouse", "Duplex"],
            "commercial": ["Hotel", "Office", "Commercial Building", "Retail", "Outlet", "Showroom"],
            "land": ["Residential Land", "Commercial Land", "Agricultural Land", "Industrial Land", "Mixed Use Land"],
            "price_currency": ["EUR"],
        }
    });
    const [property, setProperty] = useState<PropertyObj>(props.property ? props.property : {});
    const [sizeValue, setSizeValue] = useState<any>(props.property.size ? props.property.size : {});
    const { t } = useTranslation();

    useEffect(() => {
        props.onchangeProperty(property)
    }, [property])

    useEffect(() => {
        if (props.property?.address?.country) {
            setProperty((prev) => ({
                ...prev,
                price: {
                    ...prev.price,
                    currency: listCountry[props.property.address.country]?.price_currency?.[0] || "",
                },
            }));
        }
    }, [props.property?.address?.country]);

    useEffect(() => {
        props.onchangeSize(sizeValue)
    }, [sizeValue])

    // Initialize category from property object if it exists
    useEffect(() => {
        if (props.property?.category) {
            setProperty((prev) => ({
                ...prev,
                category: props.property?.category,
            }));
        }
    }, [props.property?.category]);


    const handleChange = (event: any) => {
        const { name, value } = event.target;
        setProperty((prev: any) => {
            let updated = { ...prev, [name]: value };

            // Reset fields when category changes
            if (name === 'category') {
                if (prev.category === 'residential' && value !== 'residential') {
                    updated.bedroom = '';
                    updated.bathroom = '';
                }
            }

            // Reset fields when offeringType changes
            if (name === 'offeringType') {
                if (prev.offeringType === 'for-rent' && value !== 'for-rent') {
                    updated.rentalPeriod = '';
                    updated.numberOfCheques = '';
                }
                if (prev.offeringType === 'for-sale' && value !== 'for-sale') {
                    updated.paymentMethod = '';
                    updated.downPayment = { amount: '', currency: '' };
                    updated.mortgageAvailable = '';
                    updated.yearsofmortgage = '';
                }
            }

            // Reset payment-related fields when paymentMethod changes
            if (name === 'paymentMethod') {
                if (prev.paymentMethod === 'installment' && value !== 'installment') {
                    updated.downPayment = { amount: '', currency: '' };
                }
            }

            // Reset mortgage fields if mortgageAvailable is changed
            if (name === 'mortgageAvailable' && value !== 'yes') {
                updated.yearsofmortgage = '';
            }

            return updated;
        });
    };

    // Price

    const handleChangePrice = (event: any) => {
        const { name, value } = event.target;
        setProperty((prevOptions: any) => ({
            ...prevOptions,
            price: {
                ...prevOptions.price,
                [name]: value
            },
        }));
    }

    // Down Payment

    const handleChangeDownPayment = (event: any) => {
        const { name, value } = event.target;
        setProperty((prevOptions: any) => ({
            ...prevOptions,
            downPayment: {
                ...prevOptions.downPayment,
                [name]: value
            },
        }));
    }

    // Size

    const handleChangeSize = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = event.target;
        setSizeValue((prevOptions: any) => ({
            ...prevOptions,
            [name]: name.includes("Size") ? parseFloat(value) || 0 : value,
        }));
    };

    return (
        <div className="row card-form mb-3">

            <div className="col-md-4">
                <label className="form-label-1 mb-2" htmlFor="selectTypeOpt">Property Category *</label>
                <FormControl fullWidth>
                    <Select
                        className={`input-property mb-3 ${props.errors.category ? 'input-error' : ''}`}
                        displayEmpty
                        inputProps={{ 'aria-label': 'Select' }}
                        value={property.category || ''}
                        name="category"
                        onChange={handleChange}
                    >
                        <MenuItem value="" disabled>{t("propertyDetails.select")}</MenuItem>
                        <MenuItem value={"residential"}>Residential</MenuItem>
                        <MenuItem value={"commercial"}>Commercial</MenuItem>
                        <MenuItem value={"land"}>Land</MenuItem>
                    </Select>
                </FormControl>
            </div>

            <div className="col-md-4">
                <label className="form-label-1 mb-2" htmlFor="selectTypeOpt">{t("propertyDetails.propertyType")} *</label>
                <FormControl fullWidth>
                    <Select
                        className={`input-property mb-3 ${props.errors.propertyType ? 'input-error' : ''}`}
                        displayEmpty
                        inputProps={{ 'aria-label': 'Select' }}
                        value={property.propertyType || ''}
                        name="propertyType"
                        onChange={handleChange}
                    >
                        <MenuItem value="" disabled>
                            {t("propertyDetails.select")}
                        </MenuItem>
                        {props.property?.address?.country && listCountry[props.property?.address?.country]?.[property.category as any]?.map((type: string, index: number) => (
                            <MenuItem value={type} key={index}>{type}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>

            <div className="col-md-4">
                <label className="form-label-1 mb-2" htmlFor="selectTypeOpt">{t("propertyDetails.offeringType")} *</label>
                <FormControl fullWidth>
                    <Select
                        className={`input-property mb-3 ${props.errors.offeringType ? 'input-error' : ''}`}
                        displayEmpty
                        inputProps={{ 'aria-label': 'Select' }}
                        value={property.offeringType || ''}
                        name="offeringType"
                        onChange={handleChange}
                    >
                        <MenuItem value="" disabled>{t("propertyDetails.select")}</MenuItem>
                        <MenuItem value={"for-sale"}>{t("propertyDetails.sale")}</MenuItem>
                        <MenuItem value={"for-rent"}>{t("propertyDetails.rent")}</MenuItem>
                    </Select>
                </FormControl>
            </div>

            {property.category === 'residential' &&
                <div className="col-md-4">
                    <label className="form-label-1 mb-2" htmlFor="selectTypeOpt">{t("propertyDetails.bedrooms")} *</label>
                    <FormControl fullWidth>
                        <Select
                            className={`input-property mb-3 ${props.errors.bedroom ? 'input-error' : ''}`}
                            displayEmpty
                            inputProps={{ 'aria-label': 'Select' }}
                            value={property.bedroom || ''}
                            name="bedroom"
                            onChange={handleChange}
                        >
                            <MenuItem value="" disabled>{t("propertyDetails.select")}</MenuItem>
                            <MenuItem value={"Studio"}>{t("propertyDetails.studio")}</MenuItem>
                            <MenuItem value={"1"}>1</MenuItem>
                            <MenuItem value={"2"}>2</MenuItem>
                            <MenuItem value={"3"}>3</MenuItem>
                            <MenuItem value={"4"}>4</MenuItem>
                            <MenuItem value={"5"}>5</MenuItem>
                            <MenuItem value={"6"}>6</MenuItem>
                            <MenuItem value={"7"}>7</MenuItem>
                            <MenuItem value={"8"}>8</MenuItem>
                            <MenuItem value={"9"}>9</MenuItem>
                            <MenuItem value={"10"}>10</MenuItem>
                            <MenuItem value={"11"}>11</MenuItem>
                        </Select>
                    </FormControl>
                </div>}

            {property.category === 'residential' &&
                <div className="col-md-4">
                    <label className="form-label-1 mb-2" htmlFor="selectTypeOpt">{t("propertyDetails.bathrooms")} *</label>
                    <FormControl fullWidth>
                        <Select
                            className={`input-property mb-3 ${props.errors.bathroom ? 'input-error' : ''}`}
                            displayEmpty
                            inputProps={{ 'aria-label': 'Select' }}
                            value={property.bathroom || ''}
                            name="bathroom"
                            onChange={handleChange}
                        >
                            <MenuItem value="" disabled> {t("propertyDetails.select")} </MenuItem>
                            <MenuItem value={"1"}>1</MenuItem>
                            <MenuItem value={"2"}>2</MenuItem>
                            <MenuItem value={"3"}>3</MenuItem>
                            <MenuItem value={"4"}>4</MenuItem>
                            <MenuItem value={"5"}>5</MenuItem>
                            <MenuItem value={"6"}>6</MenuItem>
                            <MenuItem value={"7"}>7</MenuItem>
                            <MenuItem value={"8"}>8</MenuItem>
                            <MenuItem value={"9"}>9</MenuItem>
                            <MenuItem value={"10"}>10</MenuItem>
                            <MenuItem value={"11"}>11</MenuItem>
                        </Select>
                    </FormControl>
                </div>
            }

            <div className="col-md-4">
                <label className="form-label-1 mb-2" htmlFor="selectTypeOpt">{t("propertyDetails.furnishingType")} *</label>
                <FormControl fullWidth>
                    <Select
                        className={`input-property mb-3 ${props.errors.furnishingType ? 'input-error' : ''}`}
                        displayEmpty
                        inputProps={{ 'aria-label': 'Select' }}
                        value={property.furnishingType || ''}
                        name="furnishingType"
                        onChange={handleChange}
                    >
                        <MenuItem value="" disabled> {t("propertyDetails.select")} </MenuItem>
                        <MenuItem value={"furnished"}>{t("propertyDetails.furnished")}</MenuItem>
                        <MenuItem value={"unfurnished"}>{t("propertyDetails.unfurnished")}</MenuItem>
                    </Select>
                </FormControl>
            </div>

            {/* Rental Period - Only show for rent */}
            {property.offeringType === 'for-rent' && (
                <div className="col-md-4">
                    <label className="form-label-1 mb-2" htmlFor="selectTypeOpt">Rental Period</label>
                    <FormControl fullWidth>
                        <Select
                            className={`input-property mb-3 ${props.errors.rentalPeriod ? 'input-error' : ''}`}
                            displayEmpty
                            inputProps={{ 'aria-label': 'Select' }}
                            value={property.rentalPeriod || ''}
                            name="rentalPeriod"
                            onChange={handleChange}
                        >
                            <MenuItem value="" disabled>{t("propertyDetails.select")}</MenuItem>
                            <MenuItem value={"daily"}>Daily</MenuItem>
                            <MenuItem value={"monthly"}>Monthly</MenuItem>
                            <MenuItem value={"yearly"}>Yearly</MenuItem>
                        </Select>
                    </FormControl>
                </div>
            )}

            {/* Payment Method - Only show for sale */}
            {property.offeringType === 'for-sale' && (
                <div className="col-md-4">
                    <label className="form-label-1 mb-2" htmlFor="selectTypeOpt">Payment Method</label>
                    <FormControl fullWidth>
                        <Select
                            className={`input-property mb-3 ${props.errors.paymentMethod ? 'input-error' : ''}`}
                            displayEmpty
                            inputProps={{ 'aria-label': 'Select' }}
                            value={property.paymentMethod || ''}
                            name="paymentMethod"
                            onChange={handleChange}
                        >
                            <MenuItem value="" disabled>{t("propertyDetails.select")}</MenuItem>
                            <MenuItem value={"cash"}>Cash</MenuItem>
                            <MenuItem value={"installment"}>Installment</MenuItem>
                        </Select>
                    </FormControl>
                </div>
            )}

            <div className="col-md-4">
                <label className="form-label-1 mb-2" htmlFor="selectTypeOpt">{t("propertyDetails.price")} *</label>
                <div className={`currency-input form-input mb-3 ${props.errors.currency || props.errors.amount ? 'input-error' : ''}`}>
                    <input type="number" min="0" placeholder={t("propertyDetails.price")} value={property.price?.amount || ''} name="amount" onChange={handleChangePrice} />
                    <select value={property.price?.currency || ""} onChange={handleChangePrice} name="currency">
                        <option value="" disabled>{t("propertyDetails.select")}</option>
                        {props.property?.address?.country &&
                            listCountry[props.property.address.country]?.price_currency?.map((currency: string, index: number) => (
                                <option value={currency} key={index}>{currency}</option>
                            ))
                        }
                    </select>
                </div>
            </div>

            {/* No. of Cheques - Only show for rent */}
            {property.offeringType === 'for-rent' && property.rentalPeriod === "yearly" && (
                <div className="col-md-4">
                    <label className="form-label-1 mb-2" htmlFor="selectTypeOpt">No. of Cheques</label>
                    <FormControl fullWidth>
                        <Select
                            className={`input-property mb-3 ${props.errors.numberOfCheques ? 'input-error' : ''}`}
                            displayEmpty
                            inputProps={{ 'aria-label': 'Select' }}
                            value={property.numberOfCheques || ''}
                            name="numberOfCheques"
                            onChange={handleChange}
                        >
                            <MenuItem value="" disabled>{t("propertyDetails.select")}</MenuItem>
                            {Array.from({ length: 12 }, (_, i) => (
                                <MenuItem value={String(i + 1)} key={i + 1}>{i + 1}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
            )}

            {/* Down Payment - Only show for installment */}
            {property.offeringType === 'for-sale' && property.paymentMethod === 'installment' && (
                <div className="col-md-4">
                    <label className="form-label-1 mb-2" htmlFor="selectTypeOpt">Down Payment</label>
                    <div className={`currency-input form-input mb-3 ${props.errors.downPayment ? 'input-error' : ''}`}>
                        <input type="number" min="0" placeholder="Down Payment" value={property.downPayment?.amount || ''} name="amount" onChange={handleChangeDownPayment} />
                        <select value={property.downPayment?.currency || property.price?.currency || ""} onChange={handleChangeDownPayment} name="currency">
                            <option value="" disabled>{t("propertyDetails.select")}</option>
                            {props.property?.address?.country &&
                                listCountry[props.property.address.country]?.price_currency?.map((currency: string, index: number) => (
                                    <option value={currency} key={index}>{currency}</option>
                                ))
                            }
                        </select>
                    </div>
                </div>
            )}

            {/* Mortgage Available - Only show for sale */}
            {property.offeringType === 'for-sale' && (
                <div className="col-md-4">
                    <label className="form-label-1 mb-2" htmlFor="selectTypeOpt">Mortgage Available?</label>
                    <FormControl fullWidth>
                        <Select
                            className={`input-property mb-3 ${props.errors.mortgageAvailable ? 'input-error' : ''}`}
                            displayEmpty
                            inputProps={{ 'aria-label': 'Select' }}
                            value={property.mortgageAvailable || ''}
                            name="mortgageAvailable"
                            onChange={handleChange}
                        >
                            <MenuItem value="" disabled>{t("propertyDetails.select")}</MenuItem>
                            <MenuItem value={"yes"}>Yes</MenuItem>
                            <MenuItem value={"no"}>No</MenuItem>
                        </Select>
                    </FormControl>
                </div>
            )}
            {/* Years of Mortgage - Only show if mortgage available is yes */}
            {property.offeringType === 'for-sale' && property.mortgageAvailable === 'yes' && (
                <div className="col-md-4">
                    <label className="form-label-1 mb-2" htmlFor="selectTypeOpt">Years of Mortgage</label>
                    <input type="number" min="0"
                        max="99" className={`form-input ${props.errors.yearsofmortgage ? 'input-error' : ''}`}
                        placeholder="Years of Mortgage" value={property.yearsofmortgage || ''} name="yearsofmortgage" onChange={e => {
                            let val = e.target.value;
                            if (val.length > 2) val = val.slice(0, 2);
                            if (parseInt(val, 10) > 99) val = '99';
                            setProperty((prev: any) => ({ ...prev, yearsofmortgage: val }));
                        }} />
                </div>
            )}
            <div className="col-md-4">
                <label className="form-label-1 mb-2" htmlFor="selectTypeOpt">{t("propertyDetails.plotSize")} *</label>
                <div className={`currency-input form-input mb-3 ${props.errors.plotSize ? 'input-error' : ''}`}>
                    <input type="number" min="0" placeholder={t("propertyDetails.plotSize")} value={sizeValue.plotSize || ''} name="plotSize" onChange={handleChangeSize} />
                    <select value={sizeValue.unitPlot || ''} name="unitPlot" onChange={handleChangeSize}>
                        <option disabled>{t("propertyDetails.select")}</option>
                        <option value="Sq M">SQM</option>
                        <option value="Sq Ft">SQFT</option>
                    </select>
                </div>
            </div>

            <div className="col-md-4">
                <label className="form-label-1 mb-2" htmlFor="selectTypeOpt">{t("propertyDetails.builtUpSize")} *</label>
                <div className={`currency-input form-input mb-3 ${props.errors.builtUpSize ? 'input-error' : ''}`}>
                    <input type="number" min="0" placeholder={t("propertyDetails.builtUpSize")} value={sizeValue.builtUpSize || ''} name="builtUpSize" onChange={handleChangeSize} />
                    <select value={sizeValue.unitBuiltUp || ''} name="unitBuiltUp" onChange={handleChangeSize}>
                        <option disabled>{t("propertyDetails.select")}</option>
                        <option value="Sq M">SQM</option>
                        <option value="Sq Ft">SQFT</option>
                    </select>
                </div>
            </div>
            <div className="col-md-4">
                <label className="form-label-1 mb-2" htmlFor="selectTypeOpt">{t("descriptionDetails.completionStatus")}</label>
                <FormControl fullWidth>
                    <Select
                        className={`input-property mb-3`}
                        displayEmpty
                        inputProps={{ 'aria-label': 'Select' }}
                        value={property.completionStatus || ''}
                        name="completionStatus"
                        onChange={handleChange}>
                        <MenuItem value="" disabled> {t("propertiesPage.select")} </MenuItem>
                        <MenuItem value={"Off-Plan"}>{t("descriptionDetails.offPlan")}</MenuItem>
                        <MenuItem value={"Completed"}>{t("descriptionDetails.completed")}</MenuItem>
                    </Select>
                </FormControl>
            </div>

            <div className="col-md-4 mb-3">
                <label className="form-label-1 mb-2" htmlFor="selectTypeOpt">Property Reference *
                    <ThemeProvider theme={theme}>
                        <Tooltip title="Your internal property id" arrow placement="right">
                            <button className="border-trans back-trans" > <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g clipPath="url(#clip0_528_412)">
                                    <path d="M2.66699 8C2.66699 8.78793 2.82219 9.56815 3.12371 10.2961C3.42524 11.0241 3.8672 11.6855 4.42435 12.2426C4.9815 12.7998 5.64294 13.2417 6.37089 13.5433C7.09885 13.8448 7.87906 14 8.66699 14C9.45492 14 10.2351 13.8448 10.9631 13.5433C11.691 13.2417 12.3525 12.7998 12.9096 12.2426C13.4668 11.6855 13.9087 11.0241 14.2103 10.2961C14.5118 9.56815 14.667 8.78793 14.667 8C14.667 6.4087 14.0349 4.88258 12.9096 3.75736C11.7844 2.63214 10.2583 2 8.66699 2C7.07569 2 5.54957 2.63214 4.42435 3.75736C3.29913 4.88258 2.66699 6.4087 2.66699 8Z" stroke="black" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M8.66699 6H8.67283" stroke="black" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M8 8H8.66667V10.6667H9.33333" stroke="black" strokeLinecap="round" strokeLinejoin="round" />
                                </g>
                                <defs>
                                    <clipPath id="clip0_528_412">
                                        <rect width="16" height="16" fill="white" transform="translate(0.666992)" />
                                    </clipPath>
                                </defs>
                            </svg>
                            </button>
                        </Tooltip>
                    </ThemeProvider>
                </label>
                <input type="text" id="idProperty" className={`form-input  ${props.errors.idProperty ? 'input-error' : ''}`} placeholder={"Reference"} value={property.idProperty || ""} name="idProperty"
                    onChange={handleChange} />
            </div>
        </div >
    )
}

export default PropertyDetails;